import React, { useState } from "react";
import { formatKSADate } from "./lib/ksaTime";

const UPLOAD_TYPES = [
  { id: "inv", icon: "📂", title: "الفواتير", desc: "تقرير المبيعات والفواتير من رتم", cls: "upl-inv" },
  { id: "dsum", icon: "📅", title: "ملخص المبيعات اليومي", desc: "تقرير الملخص اليومي من POS — المصدر الأدق للإجماليات والإرجاعات والإلغاءات (يومي)", cls: "upl-dsum" },
  { id: "cust", icon: "👥", title: "العملاء", desc: "ملف بيانات العملاء (يفضّل بعمود الجوال)", cls: "upl-cust" },
  { id: "prod", icon: "📦", title: "المنتجات", desc: "تقرير الأصناف والمبيعات لكل منتج", cls: "upl-prod" },
  { id: "stk", icon: "🏭", title: "المخزون", desc: "تقرير مستوى المخزون الحالي", cls: "upl-stk" },
  { id: "wst", icon: "🗑", title: "التالف", desc: "تقرير الكميات التالفة", cls: "upl-wst" },
  { id: "cost", icon: "💰", title: "تكلفة المنتجات", desc: "ملف فيه: اسم المنتج + التكلفة (ر) — يُستخدم لحسابات الربح والحملات", cls: "upl-cost" },
  { id: "purch", icon: "🛒", title: "المشتريات", desc: "تقرير المشتريات من رتم (Purchases Report)", cls: "upl-purch" },
  // v119.92 — Marketing Plan Excel upload, centralised here to keep all
  // file ingestion in one place (Roots → Settings → Uploads). Posts to
  // /api/marketing-plans/upload; cards/AI analysis still live in the
  // Marketing → "الخطط التسويقية" tab for review & approval.
  { id: "mkt_plan", icon: "📋", title: "الخطة التسويقية", desc: "ملف Excel للخطة الشهرية/السنوية — يحلّلها Claude تلقائياً ويكشف تداخلها مع خطط سابقة", cls: "upl-mkt-plan" },
];

function UploadCard({ t, allowed, updatedDate, onPickFile, onFileDrop, onDownloadTemplate }) {
  const [drag, setDrag] = useState(false);
  return (
    <div
      key={t.id}
      className={`upload-card ${allowed ? "" : "disabled"} ${drag ? "dragging" : ""}`}
      data-testid={`upload-card-${t.id}`}
      onDragOver={(e) => { if (!allowed) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setDrag(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        if (!allowed) return;
        const f = e.dataTransfer.files?.[0];
        if (!f) return;
        const lower = f.name.toLowerCase();
        const okMain = lower.endsWith(".xlsx") || lower.endsWith(".xls");
        const okPlan = t.id === "mkt_plan" && (lower.endsWith(".xlsx") || lower.endsWith(".xlsm"));
        if (!okMain && !okPlan) {
          alert("يُقبل فقط ملفات Excel (.xlsx / .xls" + (t.id === "mkt_plan" ? " / .xlsm" : "") + ")");
          return;
        }
        onFileDrop(t.id, f);
      }}
      style={drag ? { borderColor: "var(--info)", background: "var(--info-soft)" } : undefined}
    >
      <div className="upload-card-head">
        <span className="upload-card-icon">{t.icon}</span>
        <div>
          <div className="upload-card-title">{t.title}</div>
          <div className="upload-card-desc">{t.desc}</div>
        </div>
      </div>
      {updatedDate && (
        <div className="upload-card-meta">✓ آخر رفع: {updatedDate}</div>
      )}
      <button
        type="button"
        disabled={!allowed}
        className={`btn ${allowed ? "btn-primary" : "btn-soft"}`}
        onClick={() => onPickFile(t.id)}
        data-testid={`btn-pick-${t.id}`}
        style={{ marginTop: 10, width: "100%" }}
      >
        {allowed ? (drag ? "📥 أفلت الملف هنا" : `📤 رفع ${t.title}`) : "🔒 غير مسموح"}
      </button>
      {allowed && (
        <div style={{ fontSize: 10, color: "var(--muted)", textAlign: "center", marginTop: 4 }}>
          أو اسحب وأفلت الملف داخل البطاقة
        </div>
      )}
      {allowed && onDownloadTemplate && t.id !== "mkt_plan" && t.id !== "mkt" && (
        <button
          type="button"
          onClick={() => onDownloadTemplate(t.id)}
          data-testid={`btn-template-${t.id}`}
          style={{
            marginTop: 6, width: "100%", background: "transparent", border: "1px dashed var(--border)",
            borderRadius: 8, padding: "5px 8px", fontSize: 11, color: "var(--info, #1E6390)",
            cursor: "pointer", fontWeight: 700, fontFamily: "inherit",
          }}
        >
          📄 تحميل نموذج إكسل جاهز
        </button>
      )}
    </div>
  );
}

export default function UploadPanel({ canUpload, onPickFile, meta, onFileDrop, onDownloadTemplate }) {
  return (
    <div className="set-sec" data-testid="upload-panel">
      <h4>📥 رفع تقارير رتم (Excel)</h4>
      <p style={{ fontSize: 12.5, color: "var(--gy)", marginBottom: 14, lineHeight: 1.85 }}>
        ارفع تقارير رتم لتحديث البيانات والتحليلات. اضغط زر الرفع أو اسحب الملف مباشرة داخل البطاقة.
        الأزرار المعطّلة تعني أن دورك لا يسمح برفع هذا النوع.
      </p>

      <div className="upload-grid">
        {UPLOAD_TYPES.map((t) => {
          const allowed = !!canUpload[t.id];
          const updated = meta?.[t.id]?.updated_at;
          const updatedDate = updated ? formatKSADate(new Date(updated)) : null;
          return (
            <UploadCard
              key={t.id}
              t={t}
              allowed={allowed}
              updatedDate={updatedDate}
              onPickFile={onPickFile}
              onFileDrop={onFileDrop || (() => {})}
              onDownloadTemplate={onDownloadTemplate}
            />
          );
        })}
      </div>
    </div>
  );
}
